import React, { useState, useEffect } from 'react';
import { 
  Settings, QrCode, Users, ToggleLeft, ToggleRight,
  TrendingUp, Activity, Save, AlertCircle, BarChart3
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const QRLeadAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    is_enabled: false,
    max_campaigns_per_user: 5,
    max_leads_per_campaign: 1000
  });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/qr-leads/admin/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/qr-leads/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleToggle = async () => {
    try {
      const response = await api.post('/qr-leads/admin/toggle');
      setSettings({ ...settings, is_enabled: response.data.is_enabled });
      toast.success(response.data.is_enabled ? 'QR Lead Tool Enabled!' : 'QR Lead Tool Disabled');
    } catch (error) {
      toast.error('Failed to toggle');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4" data-testid="qr-lead-admin-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">QR Lead Collector Settings</h1>
          <p className="text-muted-foreground">Manage WhatsApp Lead Collection Tool</p>
        </div>

        {/* Main Toggle */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                settings.is_enabled ? 'bg-green-500/20' : 'bg-red-500/20'
              }`}>
                <QrCode className={`w-7 h-7 ${settings.is_enabled ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div>
                <h2 className="text-xl font-bold">QR Lead Collector</h2>
                <p className="text-sm text-muted-foreground">
                  {settings.is_enabled ? 'Enabled for all members' : 'Disabled - Members cannot access'}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggle}
              className={`p-3 rounded-xl transition-all ${
                settings.is_enabled 
                  ? 'bg-green-500 text-white' 
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {settings.is_enabled ? (
                <ToggleRight className="w-8 h-8" />
              ) : (
                <ToggleLeft className="w-8 h-8" />
              )}
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <QrCode className="w-4 h-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Campaigns</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_campaigns}</p>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">Active</span>
              </div>
              <p className="text-2xl font-bold">{stats.active_campaigns}</p>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-yellow-500" />
                <span className="text-xs text-muted-foreground">Total Leads</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_leads}</p>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-purple-500" />
                <span className="text-xs text-muted-foreground">Today's Leads</span>
              </div>
              <p className="text-2xl font-bold">{stats.today_leads}</p>
            </div>
          </div>
        )}

        {/* Limits Settings */}
        <div className="glass-card p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Usage Limits</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Max Campaigns Per User</label>
              <input
                type="number"
                value={settings.max_campaigns_per_user}
                onChange={(e) => setSettings({ ...settings, max_campaigns_per_user: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Leads Per Campaign</label>
              <input
                type="number"
                value={settings.max_leads_per_campaign}
                onChange={(e) => setSettings({ ...settings, max_leads_per_campaign: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-500" />
            How QR Lead Collector Works
          </h3>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-medium">Member Creates Campaign</p>
                <p className="text-sm text-muted-foreground">Sets WhatsApp number, welcome message</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-medium">QR Code Generated</p>
                <p className="text-sm text-muted-foreground">Unique QR links to landing page</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <p className="font-medium">Customer Scans QR</p>
                <p className="text-sm text-muted-foreground">Opens landing page, enters WhatsApp number</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center font-bold flex-shrink-0">4</div>
              <div>
                <p className="font-medium">Number Saved + WhatsApp Opens</p>
                <p className="text-sm text-muted-foreground">Lead saved, customer redirected to member's WhatsApp</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center font-bold flex-shrink-0">5</div>
              <div>
                <p className="font-medium">Export for Broadcast</p>
                <p className="text-sm text-muted-foreground">Member exports CSV to add numbers to broadcast list</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRLeadAdmin;
